"use client";
import React, { FC, useCallback } from "react";
import AudioRecorder from "./AudioRecorder";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { RootState } from "@/app/lib/store";
import { updateAudioRecording, uploadFile } from "@/app/lib/reducers/recruitmentReducer";
import { useTranslation } from "react-i18next";

export interface Recruitment6Props {}

const WAV_EXT = ".wav";

const toSafeBase = (key: string) =>
  key.toLowerCase().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").slice(0, 60);

const toWavName = (key: string) => `${toSafeBase(key)}${WAV_EXT}`;

const dataURLToBlob = (dataURL: string): Blob => {
  const [head, body] = dataURL.split(",");
  const mime = head.match(/:(.*?);/)?.[1] || "audio/wav";
  const bin = atob(body || "");
  const len = bin.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
  return new Blob([u8], { type: mime });
};

const Recruitment6: FC<Recruitment6Props> = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");
  const audioRecordings = useAppSelector((state: RootState) => state.recruitment.audioRecordings);
  const u = useAppSelector((state: RootState) => state.auth.user);

  // Keep stable English keys for storage; translate only labels
  const Q1 = "Can you tell us about your professional and personal experiences? Why do you think you could be a great guide and a great addition to our team?";
  const Q2 = "What do you like to do in your free time? Any Hobby? Are you passionate about something?";
  const Q3 = "If you had to describe the perfect day tour to discover your city, what would you do? What would you want to make people discover (food, secret places, must-see monuments …)?";
  const Q4 = "Tell us what you like about your city, what kind of incredible activities you can do there. Do you know its emblematic places and why not some unusual places worth visiting?";
  const Q5 = "Which of the experiences appeal to you the most? Do you have some tour suggestions for us?";

  // Store as dataURL string (compatible with your reducer/state)
  const handleAudioRecording = useCallback(
    (questionKey: string, audioBlob: Blob) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target) {
          const audioData = event.target.result as string; // dataURL
          dispatch(updateAudioRecording(questionKey, audioData));
        }
      };
      reader.readAsDataURL(audioBlob);
    },
    [dispatch]
  );

  // Upload one answer (works with Blob or base64)
  const handleUploadAudio = useCallback(
    (questionKey: string) => {
      if (!u?.uuid) return;

      type RecVal = Blob | string | undefined | null;
      const rawVal = (audioRecordings as Record<string, RecVal>)[questionKey];

      if (!rawVal) return;

      let fileBlob: Blob | null = null;
      if (rawVal instanceof Blob) {
        fileBlob = rawVal;
      } else if (typeof rawVal === "string" && rawVal.startsWith("data:")) {
        fileBlob = dataURLToBlob(rawVal);
      } else {
        // Unsupported format
        return;
      }

      // Ensure blob type + clean filename with .wav
      const wavBlob = fileBlob.type ? fileBlob : new Blob([fileBlob], { type: "audio/wav" });
      const formData = new FormData();
      formData.append("uuid", u.uuid);
      formData.append("file", wavBlob, `${questionKey}.wav`);
      formData.append("path", `${u.uuid}/`);
      formData.append("bucket", "guydeez-recruitment");
      formData.set("name", questionKey);
      formData.set("type", "audio/wav");
      formData.set("extension", WAV_EXT.replace(".", "")); // "wav"

      dispatch(uploadFile(formData));
    },
    [dispatch, audioRecordings, u?.uuid]
  );

  return (
    <>
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--g-burgundy)", lineHeight: 1.2 }}>
          {t('recruitment_upload_audio_title')}
        </h2>
        <span className="block mt-2" style={{ fontSize: 13, color: "var(--g-ink-500)" }}>
          {t('recruitment_upload_audio_subtitle')}
        </span>
      </div>

      <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

      <div className="space-y-8">
        <div className="border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-md p-4">
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--g-burgundy)", lineHeight: 1.4, marginBottom: 8 }}>{t('recruitment_audio_q1')}</span>
          <AudioRecorder
            questionKey={Q1}
            onAudioRecorded={handleAudioRecording}
            onUploadAudio={handleUploadAudio}
          />
        </div>

        <div className="border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-md p-4">
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--g-burgundy)", lineHeight: 1.4, marginBottom: 8 }}>{t('recruitment_audio_q2')}</span>
          <AudioRecorder
            questionKey={Q2}
            onAudioRecorded={handleAudioRecording}
            onUploadAudio={handleUploadAudio}
          />
        </div>

        <div className="border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-md p-4">
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--g-burgundy)", lineHeight: 1.4, marginBottom: 8 }}>{t('recruitment_audio_q3')}</span>
          <AudioRecorder
            questionKey={Q3}
            onAudioRecorded={handleAudioRecording}
            onUploadAudio={handleUploadAudio}
          />
        </div>

        <div className="border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-md p-4">
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--g-burgundy)", lineHeight: 1.4, marginBottom: 8 }}>{t('recruitment_audio_q4')}</span>
          <AudioRecorder
            questionKey={Q4}
            onAudioRecorded={handleAudioRecording}
            onUploadAudio={handleUploadAudio}
          />
        </div>

        <div className="border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-md p-4">
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--g-burgundy)", lineHeight: 1.4, marginBottom: 8 }}>{t('recruitment_audio_q5')}</span>
          <AudioRecorder
            questionKey={Q5}
            onAudioRecorded={handleAudioRecording}
            onUploadAudio={handleUploadAudio}
          />
        </div>
      </div>
    </>
  );
};

export default Recruitment6;
